import { faEye, faEyeSlash, faShieldAlt } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ActionModal, FormError, Toggle } from '@kcftech/design-system';
import {
    DashboardCreateRequest,
    DashboardPermissionsUpdateRequest,
    DashboardTemplateResponse,
    ICorporationResponse,
    ILocationResponse,
} from '@kcftech/prometheustypescriptapiclient';
import { useMutation } from '@tanstack/react-query';
import PBIIcon from 'assets/pbi-icon.svg?react';
import PBIPreview from 'assets/pbi-preview.png';
import {
    normalizePowerBiDashboardUrl,
    powerBiDashboardUrlValidation,
} from 'components/dashboard/shared';
import { Checkbox } from 'components/forms/Checkbox';
import { Textarea } from 'components/forms/Textarea';
import { TextInput } from 'components/forms/TextInput';
import { ValidatableLabel } from 'components/forms/ValidatableLabel';
import { SvgIcon } from 'components/svg_icon/SvgIcon';
import { useNotificationContext } from 'contexts/NotificationContext';
import { sdClient } from 'contexts/SDKClientContext';
import { useUser } from 'contexts/UserContext';
import { useEffect, useRef, useState } from 'react';
import { Controller, RegisterOptions, useForm } from 'react-hook-form';
import styled from 'styled-components';
import { tokens } from 'styles/tokens';
import { isUserSaas, isUserSaasSentry } from 'utils/RouteHelper';

const tabBorderWidthPixels = 2;

const descriptionRules: RegisterOptions = {
    maxLength: {
        value: 200,
        message: 'Description is too long.',
    },
};

const Tab = styled.div`
    box-sizing: border-box;
    position: relative;
    padding: 2.25rem 2.75rem;

    cursor: pointer;

    border-left: ${tabBorderWidthPixels / 2}px solid ${tokens.background.subtle};
    border-right: ${tabBorderWidthPixels / 2}px solid ${tokens.background.subtle};

    &[aria-selected='true'] {
        background: ${tokens.background.subtle};
    }

    /* pseudo-element to get the border looking good */
    &[aria-selected='true']:after {
        content: '';
        position: absolute;
        width: calc(100% + ${tabBorderWidthPixels * 2 - 0.25}px);
        left: -${tabBorderWidthPixels}px;
        bottom: -${tabBorderWidthPixels}px;
        border-bottom: ${tabBorderWidthPixels}px solid ${tokens.input.borderFocus};
    }
`;

type CreateDashboardModalProps = Readonly<{
    /**
     * Callback for when the create button is clicked
     */
    onCreate: () => void;
    /**
     * Callback for when the cancel button is clicked
     */
    onCancel: () => void;
    /**
     * The corporation the dashboard will be created under
     */
    corporation: ICorporationResponse;
    /**
     * The location the dashboard will be created under
     */
    location: ILocationResponse | undefined;
}>;

const CreateDashboardModalWrapper = styled.div`
    display: grid;
    grid-template-columns: 2fr 1fr;
`;

const TemplatePreviewSection = styled.div`
    border-right: 1px solid ${tokens.background.subtle};
    padding: 3rem 4rem;
`;

const PBIDashboardPreview = styled.div`
    height: 300px;
    margin-top: 6rem;
    background-position: center;
    background-repeat: no-repeat;
    background-size: contain;
    background-image: url(${PBIPreview});
`;

const ResponsiveText = styled.p`
    color: ${tokens.text.secondary};
    text-align: center;
    font-size: 1.6rem;
`;

type CreateDashboardForm = {
    title: string | undefined;
    powerBiUrl: string | undefined;
    template: string | undefined;
    description: string;
};

export function CreateDashboardModal({
    onCreate,
    onCancel,
    corporation,
    location,
}: CreateDashboardModalProps) {
    const { showToast } = useNotificationContext();
    const user = useUser();
    const isSaas = isUserSaas(user.user?.ApplicationRole);
    const isSaasSentry = isUserSaasSentry(user.user?.ApplicationRole);
    const [isVisible, setIsVisible] = useState(true);
    const [managedBySentry, setManagedBySentry] = useState(false);
    const [templates, setTemplates] = useState<DashboardTemplateResponse[]>();
    const powerBiUrlRef = useRef<HTMLInputElement>(null);

    const {
        control,
        register,
        handleSubmit,
        formState: { errors, isSubmitted, isValid, isValidating, isSubmitting },
        setValue,
    } = useForm<CreateDashboardForm>({
        // Revalidate the form inputs with every focus/blur to every input.
        // WARNING: This causes many more re-renders than the default behavior.
        mode: 'onSubmit',
        reValidateMode: 'onChange',
        defaultValues: {
            description: '',
            powerBiUrl: '',
        },
    });

    const onPowerBiUrlBlur = () => {
        if (powerBiUrlRef.current) {
            setValue(
                'powerBiUrl',
                normalizePowerBiDashboardUrl(powerBiUrlRef.current.value),
                { shouldValidate: isSubmitted }
            );
        }
    };

    useEffect(() => {
        const getTemplates = async () => {
            // TODO remove when we no longer need to get the power bi templates
            const dashboardTypes = location ? '4' : '3';
            const fetchedTemplates = await sdClient.getDashboardTemplates({
                filter: `DashboardTypeId in '${dashboardTypes}'`,
            });
            setTemplates(fetchedTemplates.Result);
        };
        getTemplates();
    }, [location]);

    const { mutate: createDashboard, isPending: isCreating, isError } = useMutation({
        mutationFn: async (data: CreateDashboardForm) => {
            const templateId = templates?.find((t) => t.Name.startsWith('PowerBI'))?.Id;
            const createdDashboard = await sdClient.createDashboard({
                body: new DashboardCreateRequest({
                    Name: data.title?.trim(),
                    Description: data.description.trim(),
                    TemplateId: Number(templateId),
                    CorporationId: corporation.Id,
                    LocationId: location?.Id,
                    PowerBiReportUrl: data.powerBiUrl?.trim(),
                }),
            });

            if (isSaasSentry) {
                await sdClient.updateDashboardPermissions({
                    id: createdDashboard.Id,
                    body: new DashboardPermissionsUpdateRequest({
                        IsSentryManaged:
                            createdDashboard.PowerBiReport !== undefined
                                ? true
                                : managedBySentry,
                        IsVisibleToCustomers: isVisible,
                    }),
                });
            }

            return true;
        },

        onSuccess: () => {
            onCreate();
            showToast({ type: 'success', content: 'Successfully created dashboard' });
        },

        onError: () =>
            showToast({ type: 'error', content: 'Failed to create dashboard' }),
    });

    return (
        <ActionModal
            aria-label="Create dashboard"
            title="Create Dashboard"
            contentNoPadding
            showButtons
            actionButtonText="Import"
            closeButtonText="Cancel"
            height={750}
            width={1300}
            footerContent={
                <div className="flex-row no-wrap f-14">
                    {isSaasSentry && (
                        <>
                            <span className="bold pr-10">
                                <FontAwesomeIcon icon={faShieldAlt} /> Managed by Sentry
                            </span>
                            <div className="default-check">
                                <Checkbox
                                    data-cy="sentry-checkbox"
                                    checked
                                    onChange={(e) => setManagedBySentry(e.value)}
                                    disabled
                                />
                            </div>
                            <span className="flex-row justify-between">
                                <span className="bold pl-50 pr-10">
                                    <FontAwesomeIcon
                                        icon={isVisible ? faEye : faEyeSlash}
                                    />{' '}
                                    {isVisible ? 'Visible' : 'Hidden'}
                                </span>
                                <Toggle
                                    data-cy="permissions-visible-switch"
                                    checked={isVisible}
                                    onChange={setIsVisible}
                                />
                            </span>
                        </>
                    )}
                    {isError && (
                        <FormError message="There was an error importing the dashboard. The URL may be invalid." />
                    )}
                </div>
            }
            onAction={handleSubmit((data) => {
                createDashboard(data);
            })}
            onClose={onCancel}
            actionable={
                (!isSubmitted || isValid) &&
                !(isValidating || (isSubmitting && !isCreating))
            }
        >
            {isSaas && (
                <div className="pb-2 flex-row flex-grow-1" role="tablist">
                    <Tab aria-selected role="tab">
                        <SvgIcon
                            className="pr-10"
                            component={PBIIcon}
                            size={20}
                            color={tokens.warning.default}
                        />
                        Import Dashboard
                    </Tab>
                </div>
            )}
            <CreateDashboardModalWrapper
                className="no-wrap full-height"
                data-cy="create-dashboard-modal-content"
            >
                <TemplatePreviewSection>
                    <PBIDashboardPreview />
                    <div className="px-75">
                        <ResponsiveText>
                            Sentry Dashboards can be set up by Sentry users and shared
                            with customers. Each template can be set up in Power BI, then
                            imported via URL into SMARTdiagnostics.
                        </ResponsiveText>
                    </div>
                </TemplatePreviewSection>
                <div className="m-30">
                    <ValidatableLabel
                        className="mb-30"
                        label="Sentry Dashboard URL"
                        required
                        error={errors.powerBiUrl}
                    >
                        <Controller
                            control={control}
                            name="powerBiUrl"
                            rules={powerBiDashboardUrlValidation}
                            render={({ field: { value, onChange } }) => (
                                <TextInput
                                    ref={powerBiUrlRef}
                                    autoFocus
                                    placeholder="Enter Sentry Dashboard URL"
                                    className="full-width mb-5"
                                    invalid={errors.powerBiUrl !== undefined}
                                    value={value}
                                    onChange={onChange}
                                    onBlur={onPowerBiUrlBlur}
                                />
                            )}
                        />
                    </ValidatableLabel>
                    <ValidatableLabel
                        className="mb-30"
                        label="Description"
                        error={errors.description}
                    >
                        <Textarea
                            minRows={5}
                            placeholder="Enter a description"
                            className="my-10"
                            aria-invalid={errors.description !== undefined}
                            {...register('description', descriptionRules)}
                            style={{
                                minWidth: 150,
                            }}
                        />
                    </ValidatableLabel>
                </div>
            </CreateDashboardModalWrapper>
        </ActionModal>
    );
}
